import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowUpRight, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PISOS = [
  { id: "rc", label: "R/C Jardim", short: "R/C JARDIM", sub: "Piso térreo com jardim exclusivo" },
  { id: "p1", label: "1º Piso View", short: "1º PISO VIEW", sub: "Varandas amplas e vista" },
  { id: "sotao", label: "2º Piso Rooftop", short: "2º PISO ROOFTOP", sub: "Mansardas · Cobertura de zinco" },
];

// Bloco A e B: BLOQUEADO (fases futuras). Bloco C ativo — status por (piso, tipologia)
const BLOQUEADO = { A: "bloqueado", B: "bloqueado" };

// Specs based on Bloco C — Plano de Vendas & Tabela de Preços Oficial (2026)
const PLANTAS = {
  rc: [
    {
      code: "T0", desde: "155.000 €", planta: "/plantas/rc/T0.jpg",
      subtitle: "Studio com jardim privativo",
      area: "47,13 m²", exterior: "Jardim exclusivo 70,19 m²",
      blocos: { ...BLOQUEADO, C: "disponivel" }, cLabel: "1 Disponível",
      specs: { "Área Bruta": "47,13 m²", "Jardim": "70,19 m²", "Varanda": "—", "Terraço": "—", "Arrumos": "6,14 m²", "Estacionamento": "15,75 m²", "Lugares": "1", "I.S.": "1" },
    },
    {
      code: "T1", desde: "205.000 €", planta: "/plantas/rc/T1.jpg",
      subtitle: "Um quarto, open space com jardim",
      area: "66,94 m²", exterior: "Jardim exclusivo 54,87 m²",
      blocos: { ...BLOQUEADO, C: "disponivel" }, cLabel: "2 Disponíveis",
      specs: { "Área Bruta": "66,94 m²", "Jardim": "54,87 m²", "Varanda": "—", "Terraço": "—", "Arrumos": "6,14 m²", "Estacionamento": "15,75 m²", "Lugares": "1", "I.S.": "1" },
    },
    {
      code: "T3", desde: "384.000 €", planta: "/plantas/rc/T3.jpg",
      subtitle: "Três quartos com suíte e jardim",
      area: "133,65 m²", exterior: "Jardim 220,78 m² · Suíte",
      blocos: { ...BLOQUEADO, C: "reservado" }, cLabel: "1 Reservada",
      specs: { "Área Bruta": "133,65 m²", "Jardim": "220,78 m²", "Varanda": "—", "Terraço": "—", "Arrumos": "12,18 m²", "Estacionamento": "31,40 m²", "Lugares": "2", "I.S.": "3" },
    },
    {
      code: "T4", desde: "450.000 €", planta: "/plantas/rc/T4.jpg",
      subtitle: "Premium com duas suítes e jardim",
      area: "155,38 m²", exterior: "Jardim 157,40 m² · 2 Suítes",
      blocos: { ...BLOQUEADO, C: "disponivel" }, cLabel: "1 Disponível",
      specs: { "Área Bruta": "155,38 m²", "Jardim": "157,40 m²", "Varanda": "—", "Terraço": "—", "Arrumos": "12,43 m²", "Estacionamento": "31,87 m²", "Lugares": "2", "I.S.": "3" },
    },
  ],
  p1: [
    {
      code: "T1", desde: "195.000 €", planta: "/plantas/p1/T1.jpg",
      subtitle: "Um quarto com varanda ampla",
      area: "65,05 m²", exterior: "Varanda 19,08 m²",
      blocos: { ...BLOQUEADO, C: "disponivel" }, cLabel: "2 Disponíveis",
      specs: { "Área Bruta": "65,05 m²", "Jardim": "—", "Varanda": "19,08 m²", "Terraço": "—", "Arrumos": "6,14 m²", "Estacionamento": "15,75 m²", "Lugares": "1", "I.S.": "1" },
    },
    {
      code: "T2", desde: "265.000 €", planta: "/plantas/p1/T2.jpg",
      subtitle: "Dois quartos, suíte e varanda",
      area: "87,44 m²", exterior: "Varanda 23,64 m² · Suíte",
      blocos: { ...BLOQUEADO, C: "reservado" }, cLabel: "1 Reservada",
      specs: { "Área Bruta": "87,44 m²", "Jardim": "—", "Varanda": "23,64 m²", "Terraço": "—", "Arrumos": "12,18 m²", "Estacionamento": "31,25 m²", "Lugares": "2", "I.S.": "2" },
    },
    {
      code: "T3", desde: "360.000 €", planta: "/plantas/p1/T3.jpg",
      subtitle: "Três quartos com suíte e varanda",
      area: "133,65 m²", exterior: "Varanda 30,75 m² · Suíte",
      blocos: { ...BLOQUEADO, C: "parcial" }, cLabel: "1 Disponível · 1 Reservada",
      specs: { "Área Bruta": "133,65 m²", "Jardim": "—", "Varanda": "30,75 m²", "Terraço": "—", "Arrumos": "12,18 m²", "Estacionamento": "31,25 m²", "Lugares": "2", "I.S.": "3" },
    },
  ],
  sotao: [
    {
      code: "T1", desde: "185.000 €", planta: "/plantas/sotao/T1.jpg",
      subtitle: "Um quarto sob a cobertura",
      area: "62,00 m²", exterior: "Mansarda · Cobertura zinco",
      blocos: { ...BLOQUEADO, C: "disponivel" }, cLabel: "1 Disponível",
      specs: { "Área Bruta": "62,00 m²", "Jardim": "—", "Varanda": "—", "Terraço": "—", "Arrumos": "6,14 m²", "Estacionamento": "15,75 m²", "Lugares": "1", "I.S.": "1" },
    },
    {
      code: "T2", desde: "270.000 €", planta: "/plantas/sotao/T2.jpg",
      subtitle: "Dois quartos com terraço e suíte",
      area: "80,03 m²", exterior: "Terraço 49,48 m² · Suíte",
      blocos: { ...BLOQUEADO, C: "disponivel" }, cLabel: "1 Disponível",
      specs: { "Área Bruta": "80,03 m²", "Jardim": "—", "Varanda": "—", "Terraço": "49,48 m²", "Arrumos": "5,79 m²", "Estacionamento": "29,81 m²", "Lugares": "2", "I.S.": "1" },
    },
    {
      code: "T3", desde: "375.000 €", planta: "/plantas/sotao/T3.jpg",
      subtitle: "Três quartos com terraço e suíte",
      area: "121,82 m²", exterior: "Terraço 50,62 m² · Suíte",
      blocos: { ...BLOQUEADO, C: "parcial" }, cLabel: "1 Disponível · 1 Reservada",
      specs: { "Área Bruta": "121,82 m²", "Jardim": "—", "Varanda": "—", "Terraço": "50,62 m²", "Arrumos": "13,06 m²", "Estacionamento": "33,50 m²", "Lugares": "2", "I.S.": "3" },
    },
  ],
};

const STATUS_STYLE = {
  disponivel: { label: "Disponível", bg: "bg-[#2B3B31]", text: "text-[#F9F9F6]", dot: "bg-[#F9F9F6]" },
  reservado: { label: "Reservado", bg: "bg-[#C0392B]", text: "text-[#F9F9F6]", dot: "bg-[#F9F9F6]" },
  parcial: { label: "Parcial", bg: "bg-[#B8860B]", text: "text-[#F9F9F6]", dot: "bg-[#F9F9F6]" },
  bloqueado: { label: "Bloqueado", bg: "bg-[#D6D3CB]", text: "text-[#5C5C59]", dot: "bg-[#9E9789]" },
};

export default function Tipologias({ onSelect }) {
  const [piso, setPiso] = useState("rc");
  const [active, setActive] = useState(null);
  const [apartments, setApartments] = useState([]);
  const items = PLANTAS[piso];
  const activePiso = PISOS.find((p) => p.id === piso);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/apartments`).then((r) => { if (alive) setApartments(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const statusMap = useMemo(() => {
    const map = {};
    apartments.forEach((a) => {
      const key = `${a.piso}|${a.tipologia}`;
      map[key] = map[key] || { A: [], B: [], C: [] };
      (map[key][a.bloco] || (map[key][a.bloco] = [])).push(a.status);
    });
    return map;
  }, [apartments]);

  const computeCardStatus = (pisoId, code, fallback) => {
    const g = statusMap[`${pisoId}|${code}`];
    if (!g) return { blocos: fallback.blocos, cLabel: fallback.cLabel };
    const blocos = {};
    ["A", "B", "C"].forEach((b) => {
      const arr = g[b] || [];
      if (arr.length === 0) { blocos[b] = fallback.blocos[b]; return; }
      const unique = [...new Set(arr)];
      blocos[b] = unique.length === 1 ? unique[0] : "parcial";
    });
    // cLabel from Bloco C counts
    const cCounts = {};
    (g.C || []).forEach((s) => { cCounts[s] = (cCounts[s] || 0) + 1; });
    const singular = { disponivel: ["Disponível", "Disponíveis"], reservado: ["Reservada", "Reservadas"], vendido: ["Vendida", "Vendidas"], bloqueado: ["Bloqueada", "Bloqueadas"] };
    const order = ["disponivel", "reservado", "vendido", "bloqueado"];
    const parts = order.filter((k) => cCounts[k]).map((k) => `${cCounts[k]} ${singular[k][cCounts[k] > 1 ? 1 : 0]}`);
    return { blocos, cLabel: parts.join(" · ") || fallback.cLabel };
  };

  return (
    <section id="tipologias" data-testid="tipologias-section" className="py-24 lg:py-36">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#5C5C59] mb-6">Plantas por Piso</div>
            <h2 className="font-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] text-[#1A1A1A] text-balance">
              O apartamento
              <br />
              <span className="italic-display text-[#2B3B31]">certo para si.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end">
            <p className="text-[15px] leading-relaxed text-[#5C5C59] font-light mb-6">
              Três pisos, cada um com o seu carácter. Explore as plantas por piso.
            </p>
            <a
              href="/anreal-residence-brochura.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="download-brochure-tipologias"
              className="inline-flex items-center gap-3 h-11 px-5 border border-[#2B3B31] text-[#2B3B31] text-[11px] tracking-[0.24em] uppercase hover:bg-[#2B3B31] hover:text-[#F9F9F6] transition-all duration-300"
            >
              <Download size={14} strokeWidth={1.5} />
              Descarregar Brochura
            </a>
          </div>
        </div>

        <div className="border-y border-[#E5E5DF] mb-10 lg:mb-14">
          <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-[#E5E5DF]">
            {PISOS.map((p) => (
              <button
                key={p.id}
                data-testid={`piso-tab-${p.id}`}
                onClick={() => setPiso(p.id)}
                className={`flex-1 text-left px-6 py-5 transition-colors duration-300 ${
                  piso === p.id ? "bg-[#2B3B31] text-[#F9F9F6]" : "bg-transparent text-[#1A1A1A] hover:bg-[#F1F1EC]"
                }`}
              >
                <div className="text-[10px] tracking-[0.24em] uppercase opacity-70 mb-1">
                  {p.id === "rc" ? "Piso 00" : p.id === "p1" ? "Piso 01" : "Piso 02"}
                </div>
                <div className="font-display text-lg lg:text-xl tracking-tight uppercase">{p.short}</div>
                <div className="text-[12px] opacity-75 mt-1">{p.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Legenda de estados */}
        <div className="mb-8 flex flex-wrap items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-[#5C5C59]">
          <span className="text-[#9E9789]">Estado:</span>
          {Object.entries(STATUS_STYLE).map(([k, s]) => (
            <span key={k} data-testid={`status-legend-${k}`} className={`inline-flex items-center gap-2 px-2.5 py-1 ${s.bg} ${s.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {s.label}
            </span>
          ))}
          <span className="text-[#9E9789] ml-1">· Bloco C em fase ativa</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {items.map((t0, i) => {
            const dyn = computeCardStatus(piso, t0.code, { blocos: t0.blocos, cLabel: t0.cLabel });
            const t = { ...t0, blocos: dyn.blocos, cLabel: dyn.cLabel };
            const cStatus = STATUS_STYLE[t.blocos.C] || STATUS_STYLE.bloqueado;
            const hasReserved = t.blocos.C === "reservado" || t.blocos.C === "parcial";
            return (
            <button
              key={`${piso}-${t.code}-${i}`}
              type="button"
              data-testid={`tipologia-card-${piso}-${t.code}`}
              onClick={() => setActive({ ...t, piso: activePiso.label })}
              className="group text-left bg-white border border-[#E5E5DF] hover:border-[#2B3B31]/60 transition-all duration-500 hover:-translate-y-1"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#F1F1EC]">
                <img
                  src={t.planta}
                  alt={`Planta ${t.code} - ${activePiso.label}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                />
                <div className="absolute top-4 left-4 bg-[#F9F9F6]/95 backdrop-blur-sm px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#2B3B31]">
                  {activePiso.short}
                </div>
                {hasReserved && (
                  <div data-testid={`reserved-badge-${piso}-${t.code}`} className="absolute bottom-4 left-4 flex items-center gap-2 bg-[#C0392B] text-[#F9F9F6] px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F9F9F6]" />
                    {t.blocos.C === "parcial" ? "Últimas fracções" : "Reservado"}
                  </div>
                )}
                <div className="absolute top-4 right-4 w-10 h-10 bg-[#2B3B31] text-[#F9F9F6] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                  <ArrowUpRight size={18} strokeWidth={1.5} />
                </div>
              </div>
              <div className="p-6 lg:p-7">
                <div className="flex items-baseline justify-between gap-4 mb-1">
                  <div className="font-display text-4xl lg:text-5xl text-[#2B3B31] tracking-tight">{t.code}</div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#9E9789]">{t.area}</div>
                </div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-[#9E9789]">Desde</span>
                  <span data-testid={`tipologia-price-${piso}-${t.code}`} className="font-display text-lg lg:text-xl text-[#1A1A1A] tracking-tight">{t.desde}</span>
                </div>
                <div className="text-[13px] text-[#1A1A1A] mb-4">{t.subtitle}</div>

                {/* Status por Bloco */}
                <div className="mb-4 pt-4 border-t border-[#E5E5DF]">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789] mb-2">Disponibilidade por Bloco</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["A", "B", "C"].map((b) => {
                      const s = STATUS_STYLE[t.blocos[b]] || STATUS_STYLE.bloqueado;
                      const isC = b === "C";
                      return (
                        <span
                          key={b}
                          data-testid={`bloco-status-${piso}-${t.code}-${b}`}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.16em] uppercase ${s.bg} ${s.text}`}
                        >
                          <span className={`w-1 h-1 rounded-full ${s.dot}`} />
                          <span className="font-semibold">Bloco {b}</span>
                          <span>·</span>
                          <span>{isC ? t.cLabel : s.label}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-3 border-t border-[#E5E5DF] flex items-center justify-between">
                  <span className="text-[12px] text-[#5C5C59]">{t.exterior}</span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-[#2B3B31] border-b border-[#2B3B31]/40 group-hover:border-[#2B3B31] pb-0.5">
                    Saber mais
                  </span>
                </div>
              </div>
            </button>
          );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="tipologia-dialog" className="max-w-6xl w-[95vw] p-0 gap-0 border-[#E5E5DF] bg-[#F9F9F6] rounded-none overflow-hidden">
          {active && (
            <>
              <DialogTitle className="sr-only">{active.code} — {active.piso}</DialogTitle>
              <DialogDescription className="sr-only">{active.subtitle}</DialogDescription>
              <div className="grid grid-cols-1 lg:grid-cols-5 max-h-[90vh] overflow-y-auto">
                <div className="lg:col-span-3 bg-white p-6 lg:p-10 border-b lg:border-b-0 lg:border-r border-[#E5E5DF] flex items-center justify-center">
                  <img src={active.planta} alt={`Planta ${active.code}`} className="w-full h-auto max-h-[70vh] object-contain" />
                </div>
                <div className="lg:col-span-2 p-6 sm:p-8 lg:p-10">
                  <div className="text-[11px] tracking-[0.28em] uppercase text-[#5C5C59] mb-4">{active.piso}</div>
                  <div className="font-display text-6xl lg:text-7xl text-[#2B3B31] tracking-tight leading-none mb-4">{active.code}</div>
                  <div className="font-display text-xl lg:text-2xl text-[#1A1A1A] leading-snug mb-4">{active.subtitle}</div>
                  <div className="flex items-baseline gap-3 pb-6 border-b border-[#E5E5DF]">
                    <span className="text-[10px] tracking-[0.24em] uppercase text-[#9E9789]">Desde</span>
                    <span data-testid={`tipologia-dialog-price-${active.code}`} className="font-display text-3xl text-[#2B3B31] tracking-tight">{active.desde}</span>
                  </div>

                  <div className="mt-6">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789] mb-3">Disponibilidade por Bloco</div>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {["A", "B", "C"].map((b) => {
                        const s = STATUS_STYLE[active.blocos?.[b]] || STATUS_STYLE.bloqueado;
                        const isC = b === "C";
                        return (
                          <span key={b} data-testid={`dialog-bloco-status-${b}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase ${s.bg} ${s.text}`}>
                            <span className={`w-1 h-1 rounded-full ${s.dot}`} />
                            <span className="font-semibold">Bloco {b}</span>
                            <span>·</span>
                            <span>{isC ? active.cLabel : s.label}</span>
                          </span>
                        );
                      })}
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789] mb-4">Áreas e Composição</div>
                    <ul className="divide-y divide-[#E5E5DF] border-y border-[#E5E5DF]">
                      {Object.entries(active.specs).map(([k, v]) => (
                        <li key={k} className="flex items-center justify-between gap-4 py-2.5 text-[13px]">
                          <span className="text-[#5C5C59]">{k}</span>
                          <span className={`${v === "—" ? "text-[#9E9789]" : "text-[#1A1A1A]"} font-medium`}>{v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-8 flex flex-col gap-3">
                    <button type="button" data-testid={`tipologia-dialog-cta-${active.code}`} onClick={() => { const c = active.code; const p = piso; setActive(null); setTimeout(() => onSelect && onSelect(c, p), 200); }} className="w-full inline-flex items-center justify-center gap-3 h-12 px-6 bg-[#2B3B31] text-[#F9F9F6] text-[12px] tracking-[0.24em] uppercase hover:-translate-y-[2px] transition-all duration-300">
                      Reservar {active.code} <ArrowUpRight size={16} strokeWidth={1.5} />
                    </button>
                    <a href="/anreal-residence-brochura.pdf" target="_blank" rel="noreferrer" data-testid="tipologia-dialog-brochure" className="w-full inline-flex items-center justify-center gap-3 h-12 px-6 border border-[#2B3B31] text-[#2B3B31] text-[12px] tracking-[0.24em] uppercase hover:bg-[#2B3B31] hover:text-[#F9F9F6] transition-all duration-300">
                      <Download size={14} strokeWidth={1.5} /> Brochura Completa
                    </a>
                  </div>
                  <p className="mt-5 text-[10px] text-[#9E9789] leading-relaxed">Valores e áreas referentes ao Bloco C (Fase 1). Documento não contratual. Sujeito a alterações técnicas.</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
